/**
 * ErvaPOS - Returns (Devoluções) Page
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Search,
  Loader2,
  RotateCcw,
  QrCode,
  Ticket,
  Package,
  CheckCircle,
  AlertCircle,
  Minus,
  Plus,
  Printer,
  Euro
} from 'lucide-react';
import { returnsAPI } from '../services/api';
import { toast } from 'sonner';

const extractAtcudFromScan = (rawValue) => {
  let raw = String(rawValue || '').trim();
  if (!raw) return '';

  try {
    raw = decodeURIComponent(raw);
  } catch (_) {
    // O leitor pode entregar texto que não é URL encoded.
  }

  // Alguns leitores configurados com um layout de teclado diferente enviam
  // Ç/ç ou apóstrofo no lugar de ":". Normalizar apenas separadores
  // de campos fiscais evita alterar o conteúdo do ATCUD.
  const normalized = raw
    .replace(/([A-Z][0-9]?)\s*[Çç']\s*(?=[^*]+)/gi, '$1:');

  const match = normalized.match(/(?:^|\*)G:([^*\s]+)/i);
  if (match?.[1]) return match[1].trim();

  const labelled = normalized.match(/ATCUD\s*[:=]\s*([^\s|*]+)/i);
  if (labelled?.[1]) return labelled[1].trim();

  return normalized;
};

const isSaleLookupBarcode = (value) =>
  /^S(?:[0-9]{39}|[0-9A-F]{32}|-[0-9A-Z._-]+)$/i.test(String(value || '').trim());

const PAYMENT_LABELS = {
  cash: 'Dinheiro',
  card: 'Multibanco/Cartão',
  multibanco: 'Multibanco/Cartão',
  mbway: 'MB WAY',
  transfer: 'Transferência bancária',
  check: 'Cheque',
  mixed: 'Pagamento misto',
  account_current: 'Conta corrente',
  voucher: 'Voucher',
};

export const ReturnsPage = () => {
  const [loading, setLoading] = useState(true);
  const [returns, setReturns] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showScanModal, setShowScanModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  
  const [atcudInput, setAtcudInput] = useState('');
  const [saleSearchResults, setSaleSearchResults] = useState([]);
  const [saleData, setSaleData] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returnReason, setReturnReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('original');
  const [assignVoucherToSaleCustomer, setAssignVoucherToSaleCustomer] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  
  const atcudInputRef = useRef(null);

  useEffect(() => {
    loadReturns();
  }, []);

  const loadReturns = async () => {
    try {
      setLoading(true);
      const response = await returnsAPI.getAll();
      setReturns(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar devoluções');
    } finally {
      setLoading(false);
    }
  };

  const filteredReturns = returns.filter(r => {
    if (!searchQuery) return true;
    return (
      r.return_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.sale_ncp?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.vendus_credit_note_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.voucher_code?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const openScanModal = () => {
    setShowScanModal(true);
    setAtcudInput('');
    setSaleSearchResults([]);
    setSaleData(null);
    setTimeout(() => atcudInputRef.current?.focus(), 100);
  };

  const openReturnableSale = (sale) => {
    setSaleData(sale);
    setReturnItems((sale.items || []).map(item => ({ ...item, quantity_to_return: 0 })));
    setRefundMethod('original');
    setAssignVoucherToSaleCustomer(false);
    setShowScanModal(false);
    setShowReturnModal(true);
  };

  const searchByAtcud = async () => {
    const rawScan = String(atcudInput || '').trim();
    const resolvedAtcud = extractAtcudFromScan(rawScan);

    if (!resolvedAtcud) {
      toast.error('Insira o código ATCUD ou leia o QR code');
      return;
    }

    setProcessing(true);
    try {
      const looksLikeFiscalQr = rawScan.includes('*') || /(?:^|\s)ATCUD\s*[:=]/i.test(rawScan);
      if (isSaleLookupBarcode(rawScan) || looksLikeFiscalQr) {
        const response = isSaleLookupBarcode(rawScan)
          ? await returnsAPI.getSaleByCode(rawScan)
          : await returnsAPI.getSaleByAtcud(resolvedAtcud);
        openReturnableSale(response.data);
      } else {
        const response = await returnsAPI.searchSales(rawScan);
        const results = Array.isArray(response.data) ? response.data : [];
        setSaleSearchResults(results);
        if (!results.length) toast.error('Nenhuma venda encontrada');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Venda não encontrada');
    } finally {
      setProcessing(false);
    }
  };

  const selectSaleResult = async (saleId) => {
    setProcessing(true);
    try {
      const response = await returnsAPI.getSaleById(saleId);
      openReturnableSale(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível abrir a venda');
    } finally {
      setProcessing(false);
    }
  };

  const handleAtcudKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchByAtcud();
    }
  };

  const updateReturnQuantity = (index, delta) => {
    const items = [...returnItems];
    const item = items[index];
    const maxReturnable = item.quantity_returnable || 0;
    
    let newQty = (item.quantity_to_return || 0) + delta;
    newQty = Math.max(0, Math.min(newQty, maxReturnable));
    
    items[index].quantity_to_return = newQty;
    setReturnItems(items);
  };

  const setMaxReturn = (index) => {
    const items = [...returnItems];
    items[index].quantity_to_return = items[index].quantity_returnable || 0;
    setReturnItems(items);
  };

  const calculateTotalRefund = () => {
    return returnItems.reduce((sum, item) => {
      const unitPrice = item.unit_price - (item.discount || 0) / (item.quantity || 1);
      return sum + (item.quantity_to_return || 0) * unitPrice;
    }, 0);
  };

  const processReturn = async () => {
    const itemsToReturn = returnItems.filter(item => item.quantity_to_return > 0);
    
    if (itemsToReturn.length === 0) {
      toast.error('Selecione pelo menos um item para devolver');
      return;
    }

    setProcessing(true);
    try {
      const response = await returnsAPI.create({
        sale_id: saleData.id,
        atcud: saleData.atcud || null,
        reason: returnReason,
        refund_method: refundMethod,
        voucher_customer_id: refundMethod === 'voucher' && assignVoucherToSaleCustomer
          ? saleData.customer_id
          : null,
        items: itemsToReturn.map(item => ({
          sale_item_id: item.id,
          product_id: item.product_id,
          product_name: item.product_name,
          batch_id: item.batch_id,
          lot_number: item.lot_number,
          quantity_returned: item.quantity_to_return,
          unit_price: item.unit_price,
          reason: returnReason,
        })),
      });

      toast.success(response.data.message);

      setShowReturnModal(false);
      setShowReceiptModal(false);
      setReceiptData(null);

      try {
        const returnId = response?.data?.return?.id;
        if (returnId) {
          const pdfRes = await returnsAPI.getReceiptPDF(returnId);
          const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
        }
      } catch (pdfError) {
        console.error('Erro ao abrir PDF da devolução:', pdfError);
      }

      // Reload returns list
      loadReturns();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar devolução');
    } finally {
      setProcessing(false);
    }
  };

  const viewReturnReceipt = async (returnId) => {
    try {
      const response = await returnsAPI.getReceiptPDF(returnId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao carregar talão');
    }
  };

  const viewVendusCreditNote = async (returnId) => {
    try {
      const response = await returnsAPI.getVendusCreditNotePDF(returnId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar nota de crédito oficial');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const totalRefund = calculateTotalRefund();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Devoluções</h1>
          <p className="text-slate-400">Processar devoluções e emitir vouchers</p>
        </div>
        <Button
          onClick={openScanModal}
          className="bg-emerald-500 hover:bg-emerald-600"
          data-testid="new-return-btn"
        >
          <QrCode className="w-4 h-4 mr-2" />
          Nova Devolução
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <RotateCcw className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Devoluções</p>
              <p className="text-2xl font-bold text-white">{returns.length}</p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Ticket className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Vouchers Emitidos</p>
              <p className="text-2xl font-bold text-white">
                {returns.filter(r => r.voucher_code).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Euro className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Valor Total</p>
              <p className="text-2xl font-bold text-white">
                €{returns.reduce((sum, r) => sum + (r.total_refund || 0), 0).toFixed(2)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Pesquisar por devolução, venda, NC, cliente ou voucher..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-slate-800 border-slate-700"
          data-testid="search-returns"
        />
      </div>

      {/* Returns Table */}
      <Card className="card-dark">
        <Table className="table-dark">
          <TableHeader>
            <TableRow>
              <TableHead>Nº Devolução</TableHead>
              <TableHead>Venda Original</TableHead>
              <TableHead>NC Vendus</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Itens</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>Reembolso</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredReturns.map((ret) => (
              <TableRow key={ret.id} data-testid={`return-row-${ret.id}`}>
                <TableCell>
                  <code className="px-2 py-1 bg-slate-800 rounded text-amber-400 font-mono">
                    {ret.return_number}
                  </code>
                </TableCell>
                <TableCell>
                  <span className="text-slate-300">{ret.sale_ncp || '-'}</span>
                </TableCell>
                <TableCell>
                  {ret.vendus_credit_note_number && !String(ret.vendus_credit_note_id || '').startsWith('manual_nc:') ? (
                    <button
                      type="button"
                      onClick={() => viewVendusCreditNote(ret.id)}
                      className="font-mono font-semibold text-emerald-400 underline underline-offset-2 hover:text-emerald-300"
                      title="Abrir nota de crédito oficial Vendus"
                    >
                      {ret.vendus_credit_note_number}
                    </button>
                  ) : ret.vendus_credit_note_number ? (
                    <span className="font-mono text-slate-400">{ret.vendus_credit_note_number}</span>
                  ) : (
                    <span className="text-slate-500">-</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-300">
                  {ret.customer_name || 'Consumidor Final'}
                </TableCell>
                <TableCell className="text-slate-400">
                  {ret.items?.length || 0} itens
                </TableCell>
                <TableCell>
                  <span className="font-bold text-emerald-400">
                    €{(ret.total_refund || 0).toFixed(2)}
                  </span>
                </TableCell>
                <TableCell>
                  {ret.voucher_code ? (
                    <div className="space-y-1">
                      <Badge className="badge-success font-mono">{ret.voucher_code}</Badge>
                      <p className="text-[11px] text-slate-400">
                        {ret.voucher_assigned_customer_id ? 'Associado ao cliente' : 'Ao portador'}
                      </p>
                    </div>
                  ) : ret.refund_method === 'original' ? (
                    <Badge className="border border-blue-500/40 bg-blue-500/15 text-blue-300">
                      {PAYMENT_LABELS[ret.refund_payment_method] || ret.refund_payment_method || 'Meio original'}
                    </Badge>
                  ) : (
                    <span className="text-slate-500">-</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-400">
                  {ret.created_at ? new Date(ret.created_at).toLocaleDateString('pt-PT') : '-'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => viewReturnReceipt(ret.id)}
                    className="h-8 w-8"
                    title="Abrir comprovativo interno para assinatura"
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredReturns.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  <RotateCcw className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma devolução encontrada</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Scan Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-emerald-400" />
              Iniciar Devolução
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-800 rounded-lg text-center">
              <QrCode className="w-16 h-16 mx-auto mb-3 text-emerald-400" />
              <p className="text-slate-300 mb-2">
                Leia o QR do talão ou pesquise a venda manualmente
              </p>
            </div>
            
            <div>
              <Label className="text-slate-300">Venda ou cliente</Label>
              <Input
                ref={atcudInputRef}
                value={atcudInput}
                onChange={(e) => { setAtcudInput(e.target.value); setSaleSearchResults([]); }}
                onKeyPress={handleAtcudKeyPress}
                placeholder="Nº venda, nome, telemóvel, NIF, ATCUD ou QR"
                className="bg-slate-800 border-slate-700 font-mono text-sm"
                data-testid="atcud-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Pode escrever parte do nome ou número. O QR operacional encontra-se no final do talão.
              </p>
            </div>

            {saleSearchResults.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-400">{saleSearchResults.length} venda(s) encontrada(s)</p>
                <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                  {saleSearchResults.map((sale) => (
                    <button
                      type="button"
                      key={sale.id}
                      onClick={() => selectSaleResult(sale.id)}
                      disabled={processing}
                      className="w-full text-left rounded-lg border border-slate-700 bg-slate-800 p-3 hover:border-emerald-500 hover:bg-slate-800/80 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-mono text-sm font-semibold text-emerald-400">{sale.ncp_number || 'Sem número'}</span>
                        <span className="font-semibold text-white">€{Number(sale.total || 0).toFixed(2)}</span>
                      </div>
                      <div className="mt-1 text-sm text-slate-200">{sale.customer_name || 'Consumidor Final'}</div>
                      <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-slate-400">
                        <span>{sale.completed_at ? new Date(sale.completed_at).toLocaleString('pt-PT') : '—'}</span>
                        {sale.customer_nif && <span>NIF: {sale.customer_nif}</span>}
                        {sale.customer_phone && <span>Tel: {sale.customer_phone}</span>}
                        <span>{sale.items_count || 0} artigo(s)</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScanModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button 
              onClick={searchByAtcud} 
              disabled={processing}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="search-sale-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Pesquisar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Modal */}
      <Dialog open={showReturnModal} onOpenChange={setShowReturnModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-400" />
              Processar Devolução
            </DialogTitle>
          </DialogHeader>
          
          {saleData && (
            <div className="space-y-4">
              {/* Sale Info */}
              <div className="p-4 bg-slate-800 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-slate-400">Venda Original</p>
                    <p className="text-lg font-bold text-white">{saleData.ncp_number}</p>
                    <p className="text-xs text-slate-500">
                      {saleData.completed_at ? new Date(saleData.completed_at).toLocaleString('pt-PT') : '-'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">Cliente</p>
                    <p className="text-white">{saleData.customer_name || 'Consumidor Final'}</p>
                  </div>
                </div>
              </div>

              {/* Items to Return */}
              <div>
                <Label className="text-slate-300 mb-2 block">Itens a Devolver</Label>
                <div className="space-y-2">
                  {returnItems.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${
                        item.quantity_to_return > 0 
                          ? 'bg-amber-500/10 border-amber-500/50' 
                          : 'bg-slate-800 border-slate-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-white font-medium">{item.product_name}</p>
                          <div className="flex gap-4 text-xs text-slate-500">
                            <span>Lote: {item.lot_number || 'N/A'}</span>
                            <span>Comprado: {item.quantity}</span>
                            <span>Devolvível: {item.quantity_returnable}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {item.quantity_returnable > 0 ? (
                            <>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => updateReturnQuantity(idx, -1)}
                                className="h-8 w-8 border-slate-700"
                                disabled={item.quantity_to_return === 0}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="w-8 text-center text-white font-bold">
                                {item.quantity_to_return}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => updateReturnQuantity(idx, 1)}
                                className="h-8 w-8 border-slate-700"
                                disabled={item.quantity_to_return >= item.quantity_returnable}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setMaxReturn(idx)}
                                className="border-slate-700 text-xs"
                              >
                                Tudo
                              </Button>
                            </>
                          ) : (
                            <Badge className="bg-slate-600 text-slate-300">Já devolvido</Badge>
                          )}
                        </div>
                      </div>
                      {item.quantity_to_return > 0 && (
                        <div className="mt-2 text-right text-emerald-400 font-medium">
                          Reembolso: €{((item.unit_price - (item.discount || 0) / (item.quantity || 1)) * item.quantity_to_return).toFixed(2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Reason */}
              <div>
                <Label className="text-slate-300">Motivo da Devolução (opcional)</Label>
                <Input
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Ex: Produto danificado, Troca por outro..."
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              {/* Total */}
              {totalRefund > 0 && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-300">Total do reembolso</p>
                      <p className="text-xs text-slate-400">
                        {refundMethod === 'voucher' ? 'Voucher válido por 90 dias' : 'Registo pelo meio de pagamento original'}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-400">€{totalRefund.toFixed(2)}</p>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-slate-200 mb-2 block">Forma de reembolso</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setRefundMethod('original')}
                    className={`rounded-xl border-2 p-4 text-left transition ${refundMethod === 'original' ? 'border-emerald-400 bg-emerald-500/15' : 'border-slate-600 bg-slate-800 hover:border-slate-500'}`}
                  >
                    <p className="font-bold text-white">Meio de pagamento original</p>
                    <p className="mt-1 text-sm text-slate-300">{PAYMENT_LABELS[saleData.payment_method] || saleData.payment_method || 'Meio original'}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRefundMethod('voucher')}
                    className={`rounded-xl border-2 p-4 text-left transition ${refundMethod === 'voucher' ? 'border-emerald-400 bg-emerald-500/15' : 'border-slate-600 bg-slate-800 hover:border-slate-500'}`}
                  >
                    <p className="font-bold text-white">Emitir voucher</p>
                    <p className="mt-1 text-sm text-slate-300">Código autónomo, válido por 90 dias</p>
                  </button>
                </div>
                {refundMethod === 'voucher' && saleData.customer_id && (
                  <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-600 bg-slate-800 p-3 text-sm text-slate-200">
                    <input
                      type="checkbox"
                      checked={assignVoucherToSaleCustomer}
                      onChange={(event) => setAssignVoucherToSaleCustomer(event.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-emerald-500"
                    />
                    <span>
                      Associar à ficha de <strong className="text-white">{saleData.customer_name}</strong>.
                      <span className="mt-1 block text-xs text-amber-300">Deixe desmarcado se a devolução corrige um cliente ou NIF errado.</span>
                    </span>
                  </label>
                )}
              </div>

              {/* Info */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-300">
                  <p className="font-medium text-blue-300">Informação</p>
                  <p>Os artigos físicos serão repostos no lote original; serviços não alteram stock. {refundMethod === 'voucher'
                    ? 'O voucher será emitido ao portador, salvo associação explícita acima.'
                    : 'Confirme que o valor foi devolvido operacionalmente pelo meio original; o FamaPOS guardará o registo.'}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button 
              onClick={processReturn} 
              disabled={processing || totalRefund === 0}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="process-return-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirmar Devolução
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Talão de Devolução</DialogTitle>
          </DialogHeader>
          
          {receiptData && (
            <div className="bg-white text-black p-4 rounded-lg font-mono text-sm">
              <div className="text-center mb-4">
                <p className="font-bold text-lg">
                  {receiptData.company?.name || receiptData.return?.return_number?.split('/')[0] || 'DEVOLUÇÃO'}
                </p>
                {receiptData.company?.address && (
                  <p className="text-xs">{receiptData.company.address}</p>
                )}
              </div>

              <div className="border-t border-b border-gray-300 py-2 my-2 text-center">
                <p className="font-bold">DEVOLUÇÃO</p>
                <p>{receiptData.return?.return_number || receiptData.voucher?.code}</p>
              </div>

              {receiptData.return?.sale_ncp && (
                <div className="text-xs mb-2">
                  <p>Venda FamaPOS original: {receiptData.return.original_app_sale_number || receiptData.return.sale_ncp}</p>
                  {receiptData.return.original_sale_is_fiscal && receiptData.return.original_document_number && (
                    <p>Documento fiscal original: {receiptData.return.original_document_number}</p>
                  )}
                </div>
              )}

              <div className="border-t border-gray-300 pt-2 mt-2">
                <p className="font-bold mb-1">Itens Devolvidos:</p>
                {(receiptData.return?.items || []).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span>{item.quantity_returned}x {item.product_name?.substring(0, 20)}</span>
                    <span>€{(item.refund_amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-300 mt-2 pt-2 font-bold">
                <div className="flex justify-between">
                  <span>TOTAL REEMBOLSO</span>
                  <span>€{(receiptData.return?.total_refund || receiptData.voucher?.value || 0).toFixed(2)}</span>
                </div>
              </div>

              {(receiptData.voucher || receiptData.return?.voucher_code) && (
                <div className="border-t border-gray-300 mt-3 pt-3 text-center">
                  <p className="font-bold text-lg mb-1">VOUCHER DE CRÉDITO</p>
                  <p className="text-2xl font-bold tracking-widest">
                    {receiptData.voucher?.code || receiptData.return?.voucher_code}
                  </p>
                  <p className="text-lg font-bold mt-1">
                    €{(receiptData.voucher?.value || receiptData.return?.total_refund || 0).toFixed(2)}
                  </p>
                  <p className="text-xs mt-2">
                    Válido até: {receiptData.voucher?.valid_until || 
                      new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-PT')}
                  </p>
                </div>
              )}

              <div className="text-center mt-4 text-xs text-gray-500">
                <p>Apresente este voucher no ato da compra</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptModal(false)} className="border-slate-700">
              Fechar
            </Button>
            <Button onClick={() => window.print()} className="bg-emerald-500 hover:bg-emerald-600">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReturnsPage;
